<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import mcpTools from 'dashboard/api/captain/mcpTools';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const { t } = useI18n();

const dialogRef = ref(null);
const connector = ref(null);
const tools = ref([]);
const isFetching = ref(false);

const parseAttachmentPaths = value =>
  value
    .split(',')
    .map(path => path.trim())
    .filter(Boolean);

const open = async selectedConnector => {
  connector.value = selectedConnector;
  tools.value = [];
  isFetching.value = true;
  dialogRef.value.open();

  try {
    const { data } = await mcpTools.getTools(selectedConnector.id);
    tools.value = data.map(tool => {
      const attachmentPathsInput = (tool.attachment_paths || []).join(', ');
      return {
        ...tool,
        attachmentPathsInput,
        serverState: {
          enabled: tool.enabled,
          write: tool.write,
          attachmentPathsInput,
        },
      };
    });
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) ||
        t('CAPTAIN.MCP_CONNECTORS.TOOLS.SAVE_ERROR')
    );
  } finally {
    isFetching.value = false;
  }
};

const saveTool = async tool => {
  try {
    const { data } = await mcpTools.updateTool(connector.value.id, tool.id, {
      enabled: tool.enabled,
      write: tool.write,
      attachment_paths: parseAttachmentPaths(tool.attachmentPathsInput),
    });
    Object.assign(tool, data);
    tool.attachmentPathsInput = (data.attachment_paths || []).join(', ');
    tool.serverState = {
      enabled: tool.enabled,
      write: tool.write,
      attachmentPathsInput: tool.attachmentPathsInput,
    };
    useAlert(t('CAPTAIN.MCP_CONNECTORS.TOOLS.SAVE_SUCCESS'));
  } catch (error) {
    // The Switch component already flips its v-model before emitting
    // `change`, so reverting to the pre-emit values here would be a no-op.
    // Restore the last server-confirmed state instead.
    Object.assign(tool, tool.serverState);
    useAlert(
      parseAPIErrorResponse(error) ||
        t('CAPTAIN.MCP_CONNECTORS.TOOLS.SAVE_ERROR')
    );
  }
};

const handleClose = () => {
  connector.value = null;
  tools.value = [];
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    :title="t('CAPTAIN.MCP_CONNECTORS.TOOLS.HEADER')"
    :description="connector?.title"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleClose"
  >
    <div
      v-if="isFetching"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <p
      v-else-if="!tools.length"
      class="py-10 text-sm text-center text-n-slate-11"
    >
      {{ t('CAPTAIN.MCP_CONNECTORS.TOOLS.EMPTY') }}
    </p>
    <div v-else class="flex overflow-y-auto flex-col gap-4 max-h-[60vh]">
      <div
        v-for="tool in tools"
        :key="tool.id"
        class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <div>
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ tool.title || tool.name }}
          </h4>
          <p v-if="tool.description" class="mt-0.5 text-sm text-n-slate-11">
            {{ tool.description }}
          </p>
        </div>

        <div class="flex gap-4 justify-between items-center">
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.MCP_CONNECTORS.TOOLS.ENABLED_LABEL') }}
          </span>
          <Switch v-model="tool.enabled" @change="saveTool(tool)" />
        </div>

        <div class="flex gap-4 justify-between items-center">
          <div class="flex-1 min-w-0">
            <span class="text-sm text-n-slate-12">
              {{ t('CAPTAIN.MCP_CONNECTORS.TOOLS.WRITE_LABEL') }}
            </span>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{ t('CAPTAIN.MCP_CONNECTORS.TOOLS.WRITE_HELP') }}
            </p>
          </div>
          <Switch v-model="tool.write" @change="saveTool(tool)" />
        </div>

        <Input
          v-model="tool.attachmentPathsInput"
          :label="t('CAPTAIN.MCP_CONNECTORS.TOOLS.ATTACHMENT_PATHS_LABEL')"
          :message="t('CAPTAIN.MCP_CONNECTORS.TOOLS.ATTACHMENT_PATHS_HELP')"
          @blur="saveTool(tool)"
        />
      </div>
    </div>
    <template #footer />
  </Dialog>
</template>
